import React, { useState } from 'react';
import type { RolloverClassPreview } from '../../types/rollover';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * The collapsible per-class card used in the Year Rollover wizard (redesign
 * spec, `.superpowers/sdd/2026-08-12-rollover-wizard-ux-redesign/`).
 *
 * This is the SHELL slice: a collapsed header with name + count pills that
 * expands to reveal the class's children. Everything except `expanded` and
 * `search` is controlled by the parent wizard — later tasks (4/5) replace the
 * plain child rows with the full toolbar + row UI and wire up search/bulk
 * actions; the props below are already shaped for that so this contract
 * doesn't change under them.
 */
export interface RolloverClassCardProps {
  cls: RolloverClassPreview;
  leaveSet: Set<string>; // studentIds currently marked leaving
  onToggleLeaver: (studentId: string) => void; // flip one child
  onBulk: (mode: 'leaving' | 'continuing', studentIds: string[]) => void; // whole (filtered) set
  keepEmpty: boolean; // "keep the class?" answer
  onKeepEmptyChange: (keep: boolean) => void;
  startDate: string; // current per-class term start (controlled)
  endDate: string;
  onDateChange: (field: 'startDate' | 'endDate', value: string) => void;
}

const RolloverClassCard: React.FC<RolloverClassCardProps> = ({
  cls,
  leaveSet,
  onToggleLeaver,
  onBulk,
  keepEmpty,
  onKeepEmptyChange,
  startDate,
  endDate,
  onDateChange,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');

  const continuingCount = cls.continuing.filter((c) => !leaveSet.has(c.studentId)).length;
  const leavingCount = cls.continuing.filter((c) => leaveSet.has(c.studentId)).length;
  const overAgeCount = cls.continuing.filter((c) => c.overMaxAgeByDob).length;
  // Mirrors the backend's `active` filter in applyRollover exactly
  // (src/rollover/utils/rollover.ts): a class with neither continuing nor
  // leaving children never participates in rollover, so it must never be
  // offered the "keep it?" prompt meant for all-leaving classes.
  const hasChildren = cls.continuing.length > 0 || cls.leavers.length > 0;

  const filtered = search.trim()
    ? cls.continuing.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    : cls.continuing;
  const overAge = filtered.filter((c) => c.overMaxAgeByDob);
  const withinAge = filtered.filter((c) => !c.overMaxAgeByDob);
  const filteredIds = filtered.map((c) => c.studentId);

  const renderChildRow = (child: RolloverClassPreview['continuing'][number]) => {
    const leaving = leaveSet.has(child.studentId);
    return (
      <div
        key={child.studentId}
        data-testid={`rollover-child-row-${child.studentId}`}
        className="flex items-center justify-between gap-3 py-2"
      >
        <div className="flex items-center gap-2 text-sm text-gray-900">
          <span>{child.name}</span>
          <span className="text-xs text-gray-500">
            {child.daysOfWeek.map((d) => DAY_NAMES[d]).join(' ')}
          </span>
          {child.overMaxAgeByDob && (
            <span className="text-xs text-orange-700">Age {child.ageYears}</span>
          )}
        </div>
        <span
          className="inline-flex rounded-md border overflow-hidden text-xs font-semibold"
          data-testid={`rollover-child-toggle-${child.studentId}`}
        >
          <button
            type="button"
            data-testid={`rollover-child-toggle-${child.studentId}-continuing`}
            aria-pressed={!leaving}
            onClick={() => {
              if (leaving) onToggleLeaver(child.studentId);
            }}
            className={!leaving ? 'bg-emerald-600 text-white px-3 py-1' : 'text-gray-500 px-3 py-1'}
          >
            Continuing
          </button>
          <button
            type="button"
            data-testid={`rollover-child-toggle-${child.studentId}-leaving`}
            aria-pressed={leaving}
            onClick={() => {
              if (!leaving) onToggleLeaver(child.studentId);
            }}
            className={leaving ? 'bg-amber-600 text-white px-3 py-1' : 'text-gray-500 px-3 py-1'}
          >
            Leaving
          </button>
        </span>
      </div>
    );
  };

  return (
    <div
      data-testid={`rollover-class-card-${cls.classId}`}
      className="rounded-lg border border-gray-200 bg-white"
    >
      <button
        type="button"
        data-testid={`rollover-class-toggle-${cls.classId}`}
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <span
            className={`text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
            aria-hidden="true"
          >
            ▶
          </span>
          <span className="text-sm font-medium text-gray-900">{cls.className}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid={`rollover-count-continuing-${cls.classId}`}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"
          >
            {continuingCount} continuing
          </span>
          {leavingCount > 0 && (
            <span
              data-testid={`rollover-count-leaving-${cls.classId}`}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
            >
              {leavingCount} leaving
            </span>
          )}
          {overAgeCount > 0 && (
            <span
              data-testid={`rollover-count-overage-${cls.classId}`}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
            >
              {overAgeCount} over age
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div
          className="border-t border-gray-100 px-4 py-3"
          data-testid={`rollover-class-body-${cls.classId}`}
        >
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search children…"
              data-testid={`rollover-class-search-${cls.classId}`}
              className="min-w-[10rem] flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
            />
            <button
              type="button"
              data-testid={`rollover-bulk-leaving-${cls.classId}`}
              onClick={() => onBulk('leaving', filteredIds)}
              className="rounded-md border border-amber-300 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-50"
            >
              Mark all leaving
            </button>
            <button
              type="button"
              data-testid={`rollover-bulk-continuing-${cls.classId}`}
              onClick={() => onBulk('continuing', filteredIds)}
              className="rounded-md border border-emerald-300 px-2 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-50"
            >
              Mark all continuing
            </button>
            <label className="flex items-center gap-1 text-xs text-gray-600">
              Start
              <input
                type="date"
                value={startDate}
                onChange={(e) => onDateChange('startDate', e.target.value)}
                data-testid={`rollover-term-start-${cls.classId}`}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs"
              />
            </label>
            <label className="flex items-center gap-1 text-xs text-gray-600">
              End
              <input
                type="date"
                value={endDate}
                onChange={(e) => onDateChange('endDate', e.target.value)}
                data-testid={`rollover-term-end-${cls.classId}`}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs"
              />
            </label>
          </div>
          {overAge.length > 0 && (
            <div className="mb-3">
              <div
                data-testid={`rollover-overage-group-${cls.classId}`}
                className="mb-1 text-xs font-semibold text-orange-700"
              >
                ⚠ Over the age limit next year — review these first
              </div>
              <div className="divide-y divide-gray-100">{overAge.map(renderChildRow)}</div>
            </div>
          )}
          {withinAge.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium text-gray-400">Within age limit</div>
              <div className="divide-y divide-gray-100">{withinAge.map(renderChildRow)}</div>
            </div>
          )}
          {hasChildren && continuingCount === 0 && (
            <div
              data-testid={`rollover-keep-empty-${cls.classId}`}
              className="mt-3 flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
            >
              <span className="text-sm text-gray-700">Is this class continuing next year?</span>
              <button
                type="button"
                aria-pressed={keepEmpty}
                data-testid={`rollover-keep-empty-yes-${cls.classId}`}
                onClick={() => onKeepEmptyChange(true)}
                className={
                  keepEmpty
                    ? 'rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white'
                    : 'rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-600'
                }
              >
                Yes, keep it
              </button>
              <button
                type="button"
                aria-pressed={!keepEmpty}
                data-testid={`rollover-keep-empty-no-${cls.classId}`}
                onClick={() => onKeepEmptyChange(false)}
                className={
                  !keepEmpty
                    ? 'rounded-md bg-amber-600 px-2 py-1 text-xs font-semibold text-white'
                    : 'rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-600'
                }
              >
                No, it&apos;s ending
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RolloverClassCard;
